import { useGetSettingsFooter } from '@agos/api-client/platform'
import { useEffect, useRef } from 'react'

import {
  CONSENT_EVENT,
  permittedTrackers,
  readConsent,
  trackerSettingsOf,
  type Tracker,
  type TrackerSettings,
} from './consent'

/**
 * The scripts a decision permits, injected once each.
 *
 * Separate from the banner so the banner file exports only a component — and because these two do
 * different jobs: one asks, the other obeys.
 *
 * **What is deliberately not ported: `customHeaderCode` and `customFooterCode`.** Legacy injects those
 * two admin-authored fields with `insertAdjacentHTML` into `<head>` and `<body>`, unsandboxed, with full
 * access to the customer's session — the opposite treatment to `CmsPage`, which frames admin HTML at a
 * null origin precisely so it cannot reach the token. Both are **empty** on this data, so nothing is
 * lost by leaving them out, and putting them back is a decision about who is trusted rather than a line
 * of code. Recorded in `docs/DECISIONS.md`.
 */

function injectTracker(tracker: Tracker, settings: TrackerSettings) {
  if (typeof document === 'undefined') return

  if (tracker === 'google-analytics') {
    const id = settings.googleAnalyticsId.trim()
    const loader = document.createElement('script')
    loader.async = true
    loader.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`
    document.head.appendChild(loader)
    const boot = document.createElement('script')
    // `anonymize_ip` is legacy's, and it stays: it is the one setting here that reduces what leaves.
    boot.text = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config',${JSON.stringify(id)},{anonymize_ip:true});`
    document.head.appendChild(boot)
    return
  }

  if (tracker === 'meta-pixel') {
    const id = settings.metaPixelId.trim()
    const script = document.createElement('script')
    script.text = `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init',${JSON.stringify(id)});fbq('track','PageView');`
    document.head.appendChild(script)
    return
  }

  const id = settings.tiktokPixelId.trim()
  const script = document.createElement('script')
  script.text = `!function(w,d,t){w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};var o=document.createElement("script");o.type="text/javascript",o.async=!0,o.src=i+"?sdkid="+e+"&lib="+t;var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a)};ttq.load(${JSON.stringify(id)});ttq.page();}(window,document,'ttq');`
  document.head.appendChild(script)
}

/** Loads whatever the current decision permits, once each, and again when the decision changes. */
export function useConsentedTrackers() {
  const { data } = useGetSettingsFooter()
  const loaded = useRef(new Set<Tracker>())

  useEffect(() => {
    if (data === undefined) return undefined
    const settings = trackerSettingsOf(data)

    function apply() {
      for (const tracker of permittedTrackers(readConsent(), settings)) {
        if (loaded.current.has(tracker)) continue
        loaded.current.add(tracker)
        injectTracker(tracker, settings)
      }
    }

    // Already decided in a previous session, or decided just now.
    apply()
    window.addEventListener(CONSENT_EVENT, apply)
    return () => window.removeEventListener(CONSENT_EVENT, apply)
  }, [data])
}
